import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GetStockInventoryTotal } from "../../store/slices/home";
import { GetPlStatement, GetProfitLoss } from "../../store/slices/reports";
import { GetFinancialYear } from "../../store/slices/assets";
import { toast } from "react-hot-toast";
import Loader from "../../common/Loader";
import AdminLayout from "../AdminLayout";
import moment from "moment";
import { API_BASE_URL, API_PATHS } from "../../utils/constants/api.constants";

const BalanceSheet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [profitLoss, setProfitLoss] = useState([]);
  // Keep as string for safe formatting/parsing
  const [netProfitLoss, setNetProfitLoss] = useState("0");
  const [stockInventoryTotal, setStockInventoryTotal] = useState();
  const [prevYearNetPL, setPrevYearNetPL] = useState(0);
  const [prevPLBalances, setPrevPLBalances] = useState([]);

  // Helpers to keep calculations safe and reusable
  const toNumber = (value) => {
    const parsed = parseFloat(String(value ?? "0").replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const shiftYear = (dateString, deltaYears) => {
    if (!dateString) return dateString;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    date.setFullYear(date.getFullYear() + deltaYears);
    return date.toISOString().slice(0, 10);
  };

  const normalizeFY = (raw) => {
    if (!raw) return raw;
    if (raw.includes("-") && raw.split("-").length === 3) {
      const startYear = parseInt(raw.split("-")[0]);
      return `${startYear}-${startYear + 1}`;
    }
    return raw;
  };

  const getPreviousFinancialYears = (selectedFY, firstFY) => {
    if (!selectedFY || !selectedFY.includes("-")) return [];
    
    const parts = selectedFY.split("-");
    if (parts.length !== 2) return [];
    const selectedStartYear = parseInt(parts[0]);
    if (isNaN(selectedStartYear)) return [];

    // Default: only 1 previous year if no firstFY
    let firstStartYear = selectedStartYear - 1;

    if (firstFY) {
      const fyParts = firstFY.split("-");
      // Must be exactly "YYYY-YYYY" format (2 parts)
      if (fyParts.length === 2) {
        const parsed = parseInt(fyParts[0]);
        if (!isNaN(parsed) && parsed < selectedStartYear) {
          firstStartYear = parsed;
        }
      }
    }

    const previousYears = [];
    for (let y = firstStartYear; y < selectedStartYear; y++) {
      previousYears.push(`${y}-${y + 1}`);
    }
    return previousYears;
  };

  const extractPrevPLAmount = (prevResponse) => {
    // Prefer explicit net P/L field if available
    const netPL = toNumber(
      prevResponse?.data?.net_profit_or_loss ?? prevResponse?.net_profit_or_loss
    );
    if (netPL !== 0) return netPL;

    // Fallback: derive from Reserves & Surplus group entries
    const liabilities = prevResponse?.data?.liabilities || prevResponse?.liabilities || {};
    const reserves = liabilities["Reserves & Surplus"];
    if (!Array.isArray(reserves)) return 0;

    const plEntry = reserves.find((entry) => {
      const name = (entry?.name || entry?.ledger_name || "").toLowerCase();
      return name.includes("profit") && name.includes("loss");
    });

    const rawAmount =
      plEntry?.balance ?? plEntry?.amount ?? plEntry?.closing_balance ?? 0;

    return toNumber(rawAmount);
  };

  const applyReservesAdjustment = (balanceSheetData, adjustment) => {
    if (!adjustment || !balanceSheetData) return balanceSheetData;

    const dataRoot = balanceSheetData?.data || balanceSheetData;
    const liabilities = dataRoot?.liabilities || {};
    const reserves = liabilities?.["Reserves & Surplus"];

    // If reserves missing or empty, create the group so UI can render it
    if (!Array.isArray(reserves) || reserves.length === 0) {
      const injectedReserves = [
        {
          name: "Profit & Loss (Prev FY)",
          balance: adjustment,
        },
      ];

      const updatedData = {
        ...dataRoot,
        liabilities: {
          ...liabilities,
          "Reserves & Surplus": injectedReserves,
        },
        total_liabilities: toNumber(dataRoot?.total_liabilities) + adjustment,
      };

      return balanceSheetData?.data ? { ...balanceSheetData, data: updatedData } : updatedData;
    }

    // Adjust the first entry; keep structure intact for the API response shape
    const updatedReserves = reserves.map((entry, idx) => {
      if (idx !== 0) return entry;
      const base = toNumber(entry?.balance ?? entry?.amount);
      return { ...entry, balance: base + adjustment };
    });

    const updatedLiabilities = {
      ...liabilities,
      "Reserves & Surplus": updatedReserves,
    };

    const updatedData = {
      ...dataRoot,
      liabilities: updatedLiabilities,
      total_liabilities: toNumber(dataRoot?.total_liabilities) + adjustment,
    };

    return balanceSheetData?.data ? { ...balanceSheetData, data: updatedData } : updatedData;
  };

  // Load saved financial year and dates from localStorage
  const loadSavedDates = () => {
    const savedFinancialYear = localStorage.getItem("selectedFinancialYear");

    // Ensure financial year is always correctly structured
    let startYear, endYear;
    if (savedFinancialYear && savedFinancialYear.includes("-")) {
      [startYear, endYear] = savedFinancialYear.split("-");
    } else {
      // Default to the current financial year
      const currentYear = new Date().getFullYear();
      startYear = currentYear - 1; // Example: 2024 (for 2024-2025)
      endYear = currentYear; // Example: 2025 (for 2024-2025)
    }

    return {
      financialYear: savedFinancialYear || `${startYear}-${endYear}`,
      fromDate: localStorage.getItem("savedFromDate") || `${startYear}-04-01`,
      toDate: localStorage.getItem("savedToDate") || `${endYear}-03-31`,
      minDate: `${startYear}-04-01`,
      maxDate: `${endYear}-03-31`,
    };
  };

  const {
    fromDate: initialFromDate,
    toDate: initialToDate,
    minDate,
    maxDate,
    financialYear: initialFinancialYear,
  } = loadSavedDates();

  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [financialYear, setFinancialYear] = useState(initialFinancialYear);

  useEffect(() => {
    if (financialYear) {
      localStorage.setItem("selectedFinancialYear", financialYear);
    }

    const [startYear, endYear] = financialYear.split("-");

    // Ensure dates remain within the financial year range
    const savedFromDate =
      localStorage.getItem("savedFromDate") || `${startYear}-04-01`;
    const savedToDate =
      localStorage.getItem("savedToDate") || `${endYear}-03-31`;

    setFromDate(savedFromDate);
    setToDate(savedToDate);

    if (!localStorage.getItem("savedFromDate"))
      localStorage.setItem("savedFromDate", savedFromDate);
    if (!localStorage.getItem("savedToDate"))
      localStorage.setItem("savedToDate", savedToDate);
  }, [financialYear]);

  const user = JSON.parse(localStorage.getItem("user"));
  const Name = user?.data?.company_name;
  const profile_id = user?.data?.profile_id;

  useEffect(() => {
    handleProfitLoss();
    fetchStocks();
  }, []);

  const handleProfitLoss = async (overrideFrom = null, overrideTo = null) => {
    const activeFrom = overrideFrom || fromDate;
    const activeTo = overrideTo || toDate;

    const newItem = {
      profile_id: profile_id,
      from_date: activeFrom,
      to_date: activeTo,
    };
    const newItempl = {
      profile_id: profile_id,
      from_date: activeFrom,
      to_date: activeTo,
    };

    setIsLoading(true);

    try {
      // Fetch current year balance sheet and P&L
      const [plStatementResponse, profitLossResponse] = await Promise.all([
        dispatch(GetPlStatement(newItem)).unwrap(),
        dispatch(GetProfitLoss(newItempl)).unwrap(),
      ]);

      const balanceSheetData = plStatementResponse?.data || plStatementResponse || {};
      const profitLossData = profitLossResponse?.data || profitLossResponse || {};

      // Fetch first financial year if not in localStorage
      const rawFirstFY = localStorage.getItem("firstFinancialYear");
      let firstFY = normalizeFY(rawFirstFY);
      if (firstFY && firstFY !== rawFirstFY) {
        localStorage.setItem("firstFinancialYear", firstFY);
      }

      if (!firstFY) {
        try {
          const fyData = await dispatch(GetFinancialYear({ profile_id })).unwrap();
          if (fyData?.first_financial_year) {
            firstFY = normalizeFY(fyData.first_financial_year);
            localStorage.setItem("firstFinancialYear", firstFY);
          }
        } catch (err) {
          console.error("Error fetching first financial year:", err);
        }
      }

      // Determine previous financial years based on selected financialYear
      const prevYears = getPreviousFinancialYears(financialYear, firstFY);

      // Fetch P&L for all previous years
      const prevPLResults = await Promise.all(
        prevYears.map(async (year) => {
          const [ys, ye] = year.split("-");
          const payload = {
            profile_id,
            from_date: `${ys}-04-01`,
            to_date: `${ye}-03-31`,
          };
          try {
            const response = await dispatch(GetProfitLoss(payload)).unwrap();
            const amount = extractPrevPLAmount(response);
            return { year, amount };
          } catch (err) {
            console.error(`Error fetching P&L for ${year}:`, err);
            return { year, amount: 0 };
          }
        })
      );

      // Filter out years where amount is 0
      const activePrevPL = prevPLResults.filter((item) => item.amount !== 0);
      setPrevPLBalances(activePrevPL);

      const totalPrevPLAmount = activePrevPL.reduce((sum, item) => sum + item.amount, 0);
      setPrevYearNetPL(totalPrevPLAmount);

      // Apply total previous FY P&L to current Reserves & Surplus without changing shape
      const adjustedBalanceSheet = applyReservesAdjustment(
        balanceSheetData,
        totalPrevPLAmount
      );

      const balanceSheetForUI =
        adjustedBalanceSheet?.data || adjustedBalanceSheet || balanceSheetData || {};

      setProfitLoss(balanceSheetForUI);
      setNetProfitLoss(
        profitLossData?.net_profit_or_loss ?? profitLossData?.data?.net_profit_or_loss
      );
    } catch (error) {
      console.error("Error fetching profit/loss data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStocks = async () => {
    const newItem = {
      profile_id: profile_id,
      from_date: fromDate, // Ensure from_date is sent
      to_date: toDate, // Ensure to_date is sent
    };

    try {
      const data = await dispatch(GetStockInventoryTotal(newItem)).unwrap();
      setStockInventoryTotal(data?.stock_value);
    } catch (error) {
      console.log("Error fetching stock:", error.message);
    }
  };

  const resetDates = async () => {
    const storedFinancialYear = localStorage.getItem("selectedFinancialYear"); // Example: "2022-2023"

    let startYear, endYear;

    if (storedFinancialYear) {
      // Extract the start and end year from stored financial year
      [startYear, endYear] = storedFinancialYear.split("-");
    } else {
      // If not found, determine the current financial year dynamically
      const today = new Date();
      startYear =
        today.getMonth() + 1 >= 4
          ? today.getFullYear()
          : today.getFullYear() - 1;
      endYear = parseInt(startYear) + 1;
    }

    const defaultFromDate = `${startYear}-04-01`;
    const defaultToDate = `${endYear}-03-31`;
    console.log(defaultFromDate, defaultToDate);
    // Update state
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);

    localStorage.setItem("savedFromDate", defaultFromDate);
    localStorage.setItem("savedToDate", defaultToDate);

    await handleProfitLoss(defaultFromDate, defaultToDate);
  };

  function formatDateRange(startDate, endDate) {
    const options = { day: "2-digit", month: "long", year: "numeric" };

    const formattedStartDate = new Date(startDate).toLocaleDateString(
      "en-GB",
      options
    );
    const formattedEndDate = new Date(endDate).toLocaleDateString(
      "en-GB",
      options
    );

    return `${formattedStartDate} to ${formattedEndDate}`;
  }

  const handlePDF = (item) => {
    const data = `${API_BASE_URL}${API_PATHS.pdfbalancesheet}/${fromDate}/${toDate}/${profile_id}`;
    navigate("/Pdf-Viewer", { state: { data } });
  };

  const handleFromDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate >= minDate && newDate <= maxDate) {
      setFromDate(newDate);
      localStorage.setItem("savedFromDate", newDate);
    } else {
      toast.error(
        `Select a date within the financial year ${formatFinancialYear(
          minDate,
          maxDate
        )}`
      );
    }
  };

  const handleToDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate >= minDate && newDate <= maxDate) {
      setToDate(newDate);
      localStorage.setItem("savedToDate", newDate);
    } else {
      toast.error(
        `Select a date within the financial year ${formatFinancialYear(
          minDate,
          maxDate
        )}`
      );
    }
  };

  const formatFinancialYear = (startDate, endDate) => {
    return `(${startDate.split("-").reverse().join("-")} to ${endDate
      .split("-")
      .reverse()
      .join("-")})`;
  };

  const goToGroupLedger = (groupName) => {
    if (groupName) {
      navigate(`/group-ledgers/${encodeURIComponent(groupName)}`);
    }
  };

  const netProfitLossValue = parseFloat(
    String(netProfitLoss ?? "0").replace(/,/g, "")
  );
  const shouldRenderNetProfitLoss =
    !Number.isNaN(netProfitLossValue) && netProfitLossValue !== 0;
  const prevYearNetPLValue = toNumber(prevYearNetPL);
  const shouldRenderPrevYearNetPL = prevPLBalances.length > 0;
  const hasReservesAndSurplusGroup = Object.keys(
    profitLoss?.liabilities || {}
  ).includes("Reserves & Surplus");
  const renderCurrentYearPLRow = () => {
    if (!shouldRenderNetProfitLoss) return null;
    return (
      <div
        className="d-flex justify-content-between"
        style={{ paddingLeft: 16 }}
      >
        <span>Profit &amp; Loss ({financialYear})</span>
        <span style={{ textAlign: "right" }}>
          {netProfitLossValue < 0 ? "- ₹" : "₹"}
          {Math.abs(netProfitLossValue).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </span>
      </div>
    );
  };

  const renderPrevYearsPLRows = () => {
    return prevPLBalances.map((item, index) => (
      <div
        key={index}
        className="d-flex justify-content-between"
        style={{ paddingLeft: 16 }}
      >
        <span>Profit &amp; Loss ({item.year})</span>
        <span style={{ textAlign: "right" }}>
          {item.amount < 0 ? "- ₹" : "₹"}
          {Math.abs(item.amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </span>
      </div>
    ));
  };

  // console.log("Profit Loss Data:", netProfitLoss.amount);
  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="row content-body">
        <div className="container-fluid">
          <div className="page-header">
            <div>
              <h2 class="main-content-title tx-24 mg-b-5">Balance Sheet</h2>
              <ol class="breadcrumb">
                <li class="breadcrumb-item">
                  <a href="#">Reports</a>
                </li>
                <li class="breadcrumb-item active" aria-current="page">
                  Balance Sheet
                </li>
              </ol>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-12">
              <div class="row">
                <div class="col-md-12">
                  <div class="card custom-card">
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3 form-inline">
                          <div class="form-group">
                            <label class="">From Date</label>
                            <input
                              class="form-control"
                              type="date"
                              value={fromDate}
                              onChange={handleFromDateChange}
                              min={minDate}
                              max={maxDate}
                            />
                          </div>
                        </div>
                        <div class="col-md-3 form-inline">
                          <div class="form-group">
                            <label class="">To Date</label>
                            <input
                              class="form-control"
                              type="date"
                              value={toDate}
                              onChange={handleToDateChange}
                              min={minDate}
                              max={maxDate}
                            />
                          </div>
                        </div>
                        <div class="col-md-5 form-inline">
                          <div class="form-group">
                            <button
                              type="submit"
                              class="btn btn-default"
                              onClick={() => handleProfitLoss()}
                            >
                              Submit
                            </button>
                          </div>
                          <div class="form-group">
                            <button
                              type="button"
                              className="btn btn-default"
                              onClick={resetDates}
                            >
                              Reset Date
                            </button>
                          </div>
                          <div class="form-group">
                            <button
                              type="button"
                              className="btn btn-default"
                              onClick={handlePDF}
                            >
                              Balance Sheet PDF
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-12">
                  <div className="card custom-card">
                    <div className="card-body">
                      {/* Trial Balance Table */}
                      <div
                        style={{ textAlign: "center", marginBottom: "20px" }}
                      >
                        <h3
                          style={{ fontWeight: "700" }}
                          className="text-capitalize"
                        >
                          {Name}
                        </h3>
                        <h4 style={{ fontWeight: "700" }}>Balance Sheet</h4>
                        <p>{formatDateRange(fromDate, toDate)}</p>
                      </div>

                      <table
                        className="table table-borderless"
                        style={{ width: "100%", fontSize: "12px" }}
                      >
                        <tbody>
                          <tr>
                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span className="font-weight-bold">
                                  Liabilities
                                </span>
                                <span>
                                  as to {moment(toDate).format("DD-MM-YYYY")}
                                </span>
                              </div>
                            </td>
                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span className="font-weight-bold">Assets</span>
                                <span>
                                  as at {moment(toDate).format("DD-MM-YYYY")}
                                </span>
                              </div>
                            </td>
                          </tr>

                          <tr>
                            {/* Liabilities */}
                            {/* Liabilities */}
                            <td style={{ border: "1px solid", width: "50%" }}>
                              <div className="mb-2">
                                {Object.entries(
                                  profitLoss?.liabilities || {}
                                ).map(([group, liabilities], groupIndex) => {
                                  // Sum the balances as provided by API
                                  const groupTotal = liabilities.reduce(
                                    (sum, liability) => {
                                      const amount = parseFloat(
                                        (liability.balance ?? 0).toString()
                                      );
                                      return sum + (isNaN(amount) ? 0 : amount);
                                    },
                                    0
                                  );

                                  // Display total of Reserves & Surplus group total (includes prev years P&L adjustment + current selected year P&L)
                                  let displayTotal = groupTotal;
                                  if (
                                    group === "Reserves & Surplus" &&
                                    shouldRenderNetProfitLoss
                                  ) {
                                    displayTotal += netProfitLossValue;
                                  }

                                  const renderGroupHeading = () => {
                                    return <b>{group}</b>;
                                  };

                                  return (
                                    <div key={groupIndex} className="mb-2">
                                      <div
                                        className="d-flex justify-content-between font-weight-bold"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => goToGroupLedger(group)}
                                      >
                                        {renderGroupHeading()}
                                        <span style={{ textAlign: "right" }}>
                                          {displayTotal < 0 ? "- ₹" : "₹"}
                                          {Math.abs(
                                            displayTotal
                                          ).toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                      {group === "Reserves & Surplus" && (
                                        <>
                                          {shouldRenderPrevYearNetPL &&
                                            renderPrevYearsPLRows()}
                                          {shouldRenderNetProfitLoss &&
                                            renderCurrentYearPLRow()}
                                        </>
                                      )}
                                    </div>
                                  );
                                })}

                                {/* Always show Profit & Loss inside "Reserves & Surplus"; add the group if API omits it */}
                                {!hasReservesAndSurplusGroup &&
                                  (shouldRenderPrevYearNetPL ||
                                    shouldRenderNetProfitLoss) && (
                                    <div className="mb-2">
                                      <div
                                        className="d-flex justify-content-between font-weight-bold"
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                          goToGroupLedger(
                                            "Reserves & Surplus"
                                          )
                                        }
                                      >
                                        <b>Reserves & Surplus</b>
                                        <span style={{ textAlign: "right" }}>
                                          {(() => {
                                            const amountToShow = prevYearNetPLValue + netProfitLossValue;
                                            return `${amountToShow < 0 ? "- ₹" : "₹"}${Math.abs(
                                              amountToShow
                                            ).toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                            })}`;
                                          })()}
                                        </span>
                                      </div>
                                      {shouldRenderPrevYearNetPL &&
                                        renderPrevYearsPLRows()}
                                      {shouldRenderNetProfitLoss &&
                                        renderCurrentYearPLRow()}
                                    </div>
                                  )}
                              </div>
                            </td>

                            {/* Assets */}
                            <td style={{ border: "1px solid" }}>
                              <div className="mb-2">
                                {Object.entries(profitLoss?.assets || {}).map(
                                  ([group, expenses], groupIndex) => {
                                    let groupTotal = expenses.reduce(
                                      (sum, expense) => {
                                        const amount = parseFloat(
                                          (
                                            expense.balance ??
                                            expense.amount ??
                                            "0"
                                          )
                                            .toString()
                                            .replace(/,/g, "")
                                        );
                                        return (
                                          sum + (isNaN(amount) ? 0 : amount)
                                        );
                                      },
                                      0
                                    );

                                    let flippedTotal;

                                    // Inject Net Loss into "Stock-in-Hand"
                                    // if (
                                    //   group === "Stock-in-Hand" &&
                                    //   stockInventoryTotal
                                    // ) {
                                    //   const netAmount = parseFloat(
                                    //     stockInventoryTotal || 0
                                    //   );
                                    //   groupTotal += netAmount; // ✅ Now valid because groupTotal is a `let`
                                    // }

                                    // if (
                                    //   group === "Stock-in-Hand" &&
                                    //   stockInventoryTotal
                                    // ) {
                                    //   flippedTotal = groupTotal;
                                    // } else {
                                    //   flippedTotal = -groupTotal;
                                    // }
                                    flippedTotal = groupTotal;
                                    return (
                                      <div key={groupIndex} className="mb-2">
                                        <div
                                          className="d-flex justify-content-between font-weight-bold"
                                          onClick={() => goToGroupLedger(group)}
                                          style={{ cursor: "pointer" }}
                                        >
                                          <b>{group}</b>
                                          <span style={{ textAlign: "right" }}>
                                            {flippedTotal < 0 ? "- ₹" : "₹"}
                                            {Math.abs(
                                              flippedTotal
                                            ).toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                            })}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  }
                                )}

                                {/* No Net Loss shown here on assets side */}
                              </div>
                            </td>
                          </tr>

                          {/* Totals */}
                          <tr>
                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span>Total Liabilities</span>
                                <span className="font-weight-bold">
                                  ₹
                                  {(() => {
                                    const liabilities = parseFloat(
                                      String(
                                        profitLoss?.total_liabilities ?? "0"
                                      ).replace(/,/g, "")
                                    );

                                    const netPLRaw = String(
                                      netProfitLoss ?? "0"
                                    ).replace(/,/g, "");
                                    const netPL = parseFloat(netPLRaw);

                                    const result = netPLRaw
                                      .trim()
                                      .startsWith("-")
                                      ? liabilities - Math.abs(netPL)
                                      : liabilities + netPL;

                                    return result.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    });
                                  })()}
                                </span>
                              </div>
                            </td>

                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span>Total Assets</span>
                                <span className="font-weight-bold">
                                  ₹
                                  {(() => {
                                    const assets = parseFloat(
                                      String(
                                        profitLoss?.total_assets ?? "0"
                                      ).replace(/,/g, "")
                                    );
                                    return assets.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    });
                                  })()}
                                </span>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BalanceSheet;
